<script setup lang="ts">
import Sidebarv2Header from '~/components/sidebarv2/Header.vue';
import Sidebarv2MenuItem from '~/components/sidebarv2/MenuItem.vue';
import Sidebarv2SectionTitle from '~/components/sidebarv2/SectionTitle.vue';
import Sidebarv2CasinoItems from '~/components/sidebarv2/menu/CasinoItems.vue';
import Sidebarv2SportsItems from '~/components/sidebarv2/menu/SportsItems.vue';

const emit =
  defineEmits<(e: 'menu-effect-end', payload: 'open' | 'close') => void>();

let menuEffectTimer: ReturnType<typeof setTimeout> | null = null;

const { openSupportChat, isLoadingOpenSupportChat } = useLiveSupport();

const appearance = useAppearanceStore();

const social = computed(() => {
  return normalizeStringJsonObject(appearance.appearance.social);
});

const { isCactusSportsbookActive, openBettingRulesInSportsbook } =
  useSportsBook();

const route = useRoute();

const layoutStore = useLayoutStore();
const { closeMenu, toggleMenuOpen } = layoutStore;
const isDarkTheme = computed(() => layoutStore.isDarkTheme);
const { isSidebarMenuOpen, isLayoutMobileOrTablet } = useLayout();

watch(
  () => isSidebarMenuOpen.value,
  (val) => {
    if (menuEffectTimer) clearTimeout(menuEffectTimer);
    menuEffectTimer = setTimeout(() => {
      emit('menu-effect-end', val ? 'open' : 'close');
      menuEffectTimer = null;
    }, 300);
  },
);

onUnmounted(() => {
  if (menuEffectTimer) clearTimeout(menuEffectTimer);
});

const { isAuthenticated } = storeToRefs(useAuthStore());
const { casinoNomenclature } = useCasino();
const { openRegister } = useAuth();
const { isHomeAsSportbookEnabled } = useFeatureFlagsNew();

const casinoRoute = computed(() =>
  isHomeAsSportbookEnabled.value ? '/games' : '/',
);

const sportsRoute = computed(() =>
  isHomeAsSportbookEnabled.value ? '/' : '/sports',
);

function onMenuToggle() {
  toggleMenuOpen();
}

function onMenuItemClick() {
  if (isLayoutMobileOrTablet.value) {
    closeMenu();
  }
}

const activeTab = ref<'casino' | 'sports'>('casino');

const getTabFromRoute = () => {
  if (route.path.startsWith('/sports')) {
    return 'sports';
  }
  if (isHomeAsSportbookEnabled.value && route.path === '/') {
    return 'sports';
  }

  return 'casino';
};

onMounted(() => {
  activeTab.value = getTabFromRoute();
});

watch(
  () => route.fullPath,
  () => {
    activeTab.value = getTabFromRoute();
  },
);

const linkFilter = (link: string) =>
  /^https?:\/\//i.test(link) ? link : `https://${link}`;

const socialLinks = computed(() => [
  {
    icon: 'menu-lateral-v2/instagram',
    url: social.value?.instagram
      ? linkFilter(social.value?.instagram)
      : 'https://instagram.com/apostatudo.bet.br',
    label: 'Instagram',
  },
  {
    icon: 'menu-lateral-v2/telegram',
    url: social.value?.telegram
      ? linkFilter(social.value?.telegram)
      : 'https://t.me/+ulZC4cxSwclkMjhh',
    label: 'Telegram',
  },
  {
    icon: 'menu-lateral-v2/youtube',
    url: social.value?.youtube
      ? linkFilter(social.value?.youtube)
      : 'https://youtube.com/@apostatudo.bet.br.oficial',
    label: 'YouTube',
  },
]);
</script>

<template>
  <Sidebarv2Header
    :is-sidebar-closed="!isSidebarMenuOpen"
    @close-menu="closeMenu()"
    @on-click-menu="onMenuToggle()"
  />

  <div
    :class="[
      $style.sidebarContent,
      {
        [$style.isClosed]: !isSidebarMenuOpen,
        [$style.darkTheme]: isDarkTheme,
      },
    ]"
    :draggable="false"
  >
    <!-- Abas Cassino/Esporte -->
    <div
      v-if="isSidebarMenuOpen"
      :class="[$style.tabsContainer]"
    >
      <div :class="[$style.tabsWrapper, { [$style.darkTheme]: isDarkTheme }]">
        <button
          v-tooltip.desktopOnly="casinoNomenclature"
          :class="[$style.tab, activeTab === 'casino' ? $style.active : '']"
          @click="navigateTo(casinoRoute)"
        >
          <span>{{ casinoNomenclature }}</span>
        </button>

        <button
          v-tooltip.desktopOnly="'Esporte'"
          :class="[$style.tab, activeTab === 'sports' ? $style.active : '']"
          @click="navigateTo(sportsRoute)"
        >
          <span>Esporte</span>
        </button>
      </div>
    </div>

    <!-- Conteúdo baseado na aba ativa -->
    <Sidebarv2CasinoItems
      v-if="activeTab === 'casino'"
      :is-logged-user="isAuthenticated"
      @on-click-menu="onMenuItemClick()"
      @open-register-modal="openRegister"
    />
    <Sidebarv2SportsItems
      v-else-if="activeTab === 'sports'"
      :is-logged-user="isAuthenticated"
      @on-click-menu="onMenuItemClick()"
      @open-register-modal="openRegister"
    />

    <!-- Precisa de Ajuda? -->
    <div
      v-if="isSidebarMenuOpen"
      :class="$style.helpSection"
    >
      <Sidebarv2SectionTitle title="Precisa de Ajuda" />

      <div :class="$style.helpContainer">
        <Sidebarv2MenuItem
          is-button
          @click="openSupportChat()"
        >
          <BaseLoading
            v-if="isLoadingOpenSupportChat"
            class="sidebarIcon !bg-transparent !relative"
            :show="true"
            :size="'xsmall'"
          />
          <NuxtIcon
            v-else
            class="sidebarIcon"
            name="menu-lateral-v2/suporte"
          />
          <span>{{ $t('sidebar.live_support') }}</span>
        </Sidebarv2MenuItem>

        <Sidebarv2MenuItem
          :draggable="false"
          :is-active="route.path === '/blog'"
          to="/blog"
        >
          <NuxtIcon
            class="sidebarIcon"
            name="menu-lateral-v2/blog"
          />
          <span>{{ $t('sidebar.blog') }}</span>
        </Sidebarv2MenuItem>

        <Sidebarv2MenuItem
          :draggable="false"
          target="_blank"
          to="https://atendimento.apostatudo.bet.br/"
        >
          <NuxtIcon
            class="sidebarIcon"
            name="menu-lateral-v2/central-ajuda"
          />
          <span>Help Center</span>
        </Sidebarv2MenuItem>

        <Sidebarv2MenuItem
          v-if="isCactusSportsbookActive"
          is-button
          @click="openBettingRulesInSportsbook"
        >
          <NuxtIcon
            class="sidebarIcon"
            name="file"
          />
          <span>{{ $t('sidebar.betting_rules') }}</span>
        </Sidebarv2MenuItem>
      </div>
    </div>

    <!-- Precisa de Ajuda? (menu fechado) -->
    <div
      v-if="!isSidebarMenuOpen"
      :class="[
        $style.helpContainerClosed,
        'flex flex-col justify-center items-start self-stretch p-0 w-[70px] rounded-none mx-0 border-0',
      ]"
    >
      <Sidebarv2MenuItem
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.live_support')"
        is-button
        @click="openSupportChat()"
      >
        <BaseLoading
          v-if="isLoadingOpenSupportChat"
          class="sidebarIcon !bg-transparent !relative"
          :show="true"
          :size="'xsmall'"
        />
        <NuxtIcon
          v-else
          class="sidebarIcon"
          name="menu-lateral-v2/suporte"
        />
      </Sidebarv2MenuItem>

      <Sidebarv2MenuItem
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.blog')"
        :draggable="false"
        :is-active="route.path === '/blog'"
        to="/blog"
      >
        <NuxtIcon
          class="sidebarIcon"
          name="menu-lateral-v2/blog"
        />
      </Sidebarv2MenuItem>

      <Sidebarv2MenuItem
        v-tooltip.desktopOnly.closedSideBarOnly="'Help Center'"
        :draggable="false"
        target="_blank"
        to="https://atendimento.apostatudo.bet.br/"
      >
        <NuxtIcon
          class="sidebarIcon"
          name="menu-lateral-v2/central-ajuda"
        />
      </Sidebarv2MenuItem>

      <Sidebarv2MenuItem
        v-if="isCactusSportsbookActive"
        v-tooltip.desktopOnly.closedSideBarOnly="$t('sidebar.betting_rules')"
        is-button
        @click="openBettingRulesInSportsbook"
      >
        <NuxtIcon
          class="sidebarIcon"
          name="file"
        />
      </Sidebarv2MenuItem>
    </div>

    <!-- Siga-nos (menu aberto) -->
    <div
      v-if="isSidebarMenuOpen"
      class="w-full px-[15px] flex items-center justify-start gap-[4px]"
    >
      <span class="text-white font-semibold text-[14px] leading-[18px]">
        Siga-nos:
      </span>
      <span class="w-[4px]" />
      <template
        v-for="item in socialLinks"
        :key="item.icon"
      >
        <a
          v-if="
            item.url.startsWith('http://') || item.url.startsWith('https://')
          "
          v-tooltip.desktopOnly="item.label"
          class="flex items-center justify-center w-[36px] h-[36px] rounded-[10px] hover:bg-white/10"
          :href="item.url"
          rel="noopener"
          target="_blank"
        >
          <NuxtIcon
            class="sidebarIcon text-white text-[20px]"
            :name="item.icon"
          />
        </a>
        <NuxtLink
          v-else
          v-tooltip.desktopOnly="item.label"
          class="flex items-center justify-center w-[36px] h-[36px] rounded-[10px] hover:bg-white/10"
          :to="item.url"
        >
          <NuxtIcon
            class="sidebarIcon text-white text-[20px]"
            :name="item.icon"
          />
        </NuxtLink>
      </template>
    </div>

    <!-- Siga-nos (menu fechado) -->
    <template v-if="!isSidebarMenuOpen">
      <div class="flex flex-col items-center max-w-[70px]">
        <template
          v-for="item in socialLinks"
          :key="item.icon"
        >
          <a
            v-if="
              item.url.startsWith('http://') || item.url.startsWith('https://')
            "
            v-tooltip.desktopOnly="item.label"
            class="flex justify-center items-center w-[48px] h-[48px] mb-1.5 mx-auto bg-white/5 hover:bg-white/10 rounded-[12px] border border-solid border-transparent cursor-pointer"
            :href="item.url"
            rel="noopener"
            target="_blank"
          >
            <NuxtIcon
              class="sidebarIcon text-[20px] text-white"
              :name="item.icon"
            />
          </a>
          <NuxtLink
            v-else
            v-tooltip.desktopOnly="item.label"
            class="flex justify-center items-center w-[48px] h-[48px] mb-1.5 mx-auto bg-white/5 hover:bg-white/10 rounded-[12px] border border-solid border-transparent cursor-pointer"
            :to="item.url"
          >
            <NuxtIcon
              class="sidebarIcon text-[20px] text-white"
              :name="item.icon"
            />
          </NuxtLink>
        </template>
      </div>
    </template>

    <div></div>
  </div>
</template>

<style module lang="scss">
.sidebarContent {
  @apply flex flex-col;
  @apply pt-[10px] gap-[10px] select-none;

  &.isClosed {
    @apply gap-2 pt-0;
  }

  &.darkTheme {
    @apply [background:#000000];
  }

  .tabsContainer {
    @apply border-solid border-sidebar-links/10 px-[15px];
  }

  .tabsWrapper {
    @apply flex h-[34px] items-center shrink-0 self-stretch border bg-transparent p-[4px] rounded-[8px] border-solid border-[rgba(255,255,255,0.10)] text-white text-xs font-semibold leading-4 gap-0;
    backdrop-filter: blur(3.74px);

    &.darkTheme {
      @apply [background:#000000];
    }
  }

  .tab {
    @apply flex justify-center items-center flex-[1_0_0] self-stretch py-[4px] px-[7px] rounded-[4.5px];

    &:hover {
      @apply bg-white/10;
    }

    &.active {
      @apply [background:#FF450C];
    }
  }

  .helpSection {
    @apply px-[15px] py-[9px] flex flex-col gap-[9px];
  }

  .helpContainer {
    @apply flex flex-col gap-[6px];
  }

  .helpContainerClosed {
    @apply border-solid border-b border-sidebar-links/10 select-none;
  }

  @media screen and (min-width: 1024px) {
    &.isClosed {
      .tabsContainer {
        @apply hidden;
      }

      .helpSection {
        @apply hidden;
      }
    }
  }
}
</style>
